// import globalLogger from "@lazarentertainment/js-logger"
// import { captureException } from "@sentry/nextjs"
import { useAnalytics } from "@lazarentertainment/analytics-collector"
import { captureException, captureMessage } from "@sentry/nextjs"
import { useChannelContext } from "client/components/channel"
import { useCreateCartMutation } from "client/graphql/commerce-v2-api"
import { CommerceFooter } from "client/packages/commerce/components/ui/styles/CommerceFooters"
import {
  OuterWrapper,
  ScrollWrapper,
} from "client/packages/commerce/components/ui/styles/Containers"
import { CheckoutMessage } from "client/packages/commerce/components/ui/styles/FormElementStyles"
import {
  ErrorHeading,
  SectionHeading,
  SubHeading,
} from "client/packages/commerce/components/ui/textStyles"
import { useRef, useState } from "react"
import { FormattedMessage } from "react-intl"
import BackButton from "shared/components/buttons/BackButton"
import LoaderButton from "shared/components/buttons/LoaderButton"
import { ModalText, PanelModal } from "shared/components/PanelModal"
import { Link } from "shared/styles/textStyles"
import styled from "styled-components"

import ProductHeader from "../TargetProductHeader"

import {
  formatForCartCreation,
  getCartTotalProductCount,
  useTargetCartContext,
} from "./TargetCartContext"
import CartItem from "./TargetCartItem"
import CartTotals from "./TargetCartTotals"

export type CartProps = {
  showProduct?: () => void
  currencyCode: string
}

const ItemsHeading = styled(SectionHeading)`
  font-size: 1.29rem;
  margin-top: 10px;
`

const TargetEmptyCartMessage = styled(CheckoutMessage)`
  ${ErrorHeading}, ${SubHeading} {
    font-family: "Target", Helvetica, sans-serif;
  }
`

const TargetModalText = styled(ModalText)`
  ${ErrorHeading}, ${SubHeading} {
    font-family: "Target", Helvetica, sans-serif;
  }
`

const Cart = ({ currencyCode, showProduct = () => {} }: CartProps) => {
  const { analytics } = useAnalytics()
  const { channelId, broadcastId, recordingId } = useChannelContext()
  const { contents } = useTargetCartContext()
  const totalProductCount = getCartTotalProductCount(contents)
  const windowRef = useRef<Window | null>(null)

  const [isLoading] = useState<boolean>(false)
  const [checkoutBackupUrl, setCheckoutBackupUrl] = useState<
    string | null
  >(null)

  const [createCart] = useCreateCartMutation({
    onCompleted(data) {
      const cartUrl = data.target_createCart

      if (windowRef.current) {
        // Window is open as expected, now set its URL
        windowRef.current.location = cartUrl
        return
      }

      // Checkout window doesn't exist, even though we called window.open() before calling createCart
      // Either the browser doesn't support window.open() with no URL specified,
      // Or maybe the user closed the popup window before we were ready.
      // See https://github.com/lazarentertainment/broadcast-viewer/issues/1546
      captureMessage("Checkout popup window not found")
      // Try again
      windowRef.current = window.open(cartUrl)

      if (windowRef.current === null) {
        captureMessage("Couldn't open checkout window with cart URL")
        setCheckoutBackupUrl(cartUrl)
      }
    },
    onError(error) {
      captureException(error)
    },
  })

  return (
    <OuterWrapper>
      <ProductHeader showCart={() => {}} />
      <ScrollWrapper style={{ position: "relative", flexGrow: 1 }}>
        {checkoutBackupUrl !== null && (
          <PanelModal
            closeModal={() => setCheckoutBackupUrl(null)}
            bottom
            closeButtonRobotTag="target-cart-error-modal-close-button"
          >
            <TargetModalText style={{ textAlign: "start" }}>
              <FormattedMessage
                id="cart.openCheckoutError"
                defaultMessage="We were unable to automatically open your Target cart. Please complete checkout on the Target website:"
              />
            </TargetModalText>
            <Link
              href={checkoutBackupUrl}
              target="_blank"
              rel="noreferrer"
            >
              www.target.com/cart
            </Link>
          </PanelModal>
        )}
        {totalProductCount === 0 ? (
          <TargetEmptyCartMessage>
            <ErrorHeading>
              <FormattedMessage
                id="cart.emptyHeader"
                defaultMessage="Nothing to see here"
              />
            </ErrorHeading>
            <SubHeading style={{ marginBottom: "5px" }}>
              <FormattedMessage
                id="cart.emptyText"
                defaultMessage="You have no saved items."
              />
            </SubHeading>
            <SubHeading>
              <FormattedMessage
                id="cart.emptyText2"
                defaultMessage="Save items to check out with Target."
              />
            </SubHeading>
          </TargetEmptyCartMessage>
        ) : (
          <>
            <ItemsHeading>
              <FormattedMessage
                id="cart.title"
                defaultMessage="Saved Items"
              />
            </ItemsHeading>
            {Object.values(contents).map((cartItem) => {
              return (
                <CartItem
                  key={cartItem.variantId}
                  cartItem={cartItem}
                  currencyCode={currencyCode}
                />
              )
            })}
            <CartTotals
              currencyCode={currencyCode}
              cartContents={contents}
            />
          </>
        )}
      </ScrollWrapper>
      <CommerceFooter>
        <BackButton onClick={showProduct} />
        <LoaderButton
          disabled={totalProductCount === 0}
          isLoading={isLoading}
          type="button"
          onClick={() => {
            analytics.track("target_checkout", {
              channelId,
              broadcastId,
              recordingId,
              cart: Object.keys(contents).map((key) => contents[key]),
            })

            createCart({
              variables: {
                items: formatForCartCreation(contents),
              },
            })

            // We call window.open here because on iOS, window.open is blocked in
            // async code, so we can't call it in the onCompleted callback above.
            // Instead we open the window and then set the location once we know
            // the cart URL.
            windowRef.current = window.open()
          }}
        >
          <FormattedMessage
            id="commerce.checkout"
            defaultMessage="Add to cart"
          />
        </LoaderButton>
      </CommerceFooter>
    </OuterWrapper>
  )
}

export default Cart
